import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import type { VariantSectionModel } from 'autogen/swagger/all';
import { ShowMoreVariantsButton } from 'autogen/translation-keys/trans-website-product-productvariants';
import ScrollSnapSlider from 'components/generic/ScrollSnapSlider';
import SwatchRenderer from 'components/product/Variants/Swatches/SwatchRenderer';
import { DisplayTypeSize } from 'components/product/Variants/Swatches/shared/styles';
import TruncatedGrid from 'components/TruncatedGrid';
import DefaultToggleButton from 'components/TruncatedGrid/components/DefaultToggleButton';
import { breakpoint } from 'variables';
import SectionHeader from './SectionHeader';

// #region Styles

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
`;

const Nav = styled.nav``;

const StyledSectionHeader = styled(SectionHeader)`
    white-space: normal;
`;

const StyledTruncatedGrid = styled(TruncatedGrid)`
    display: none;
    
    ${breakpoint.up('768px')} {
        display: grid;
    }
`;

const StyledScrollSnapSlider = styled(ScrollSnapSlider)`
    && {
        gap: ${({ theme }) => theme.Spacing8};
    
        ${breakpoint.up('768px')} {
            display: none;
        }
    }
`;

const GridSwatchRenderer = styled(SwatchRenderer)`
`;

const SliderSwatchRenderer = styled(SwatchRenderer)`
    flex: none;
`;

// #endregion Styles

export default function VariantsSection({
    variantsSection,
    hasMultipleVariantSections,
    ...restProps
}: {
    variantsSection: VariantSectionModel;
    hasMultipleVariantSections: boolean;
}) {
    const { variants, variantPickerButtonText } = variantsSection;
    const { displayType, productId } = variants[0];
    const selectedItem = variants.find((variant) => variant.isSelected);
    const [focusedItem, setFocusedItem] = useState(selectedItem);
    const { t } = useTranslation();

    const renderVariants = (props?: { slider: boolean }) => {
        const { slider } = props || {};

        return variants.map((variant) => {
            return (
                <GridSwatchRenderer
                    as={slider ? SliderSwatchRenderer : undefined}
                    key={variant.productId}
                    {...variant}
                    size={displayType === 'Image' ? 'sm' : 'lg'}
                    onMouseOver={() => {
                        setFocusedItem(variant);
                    }}
                    onMouseLeave={() => {
                        setFocusedItem(selectedItem);
                    }}
                    onFocus={() => {
                        setFocusedItem(variant);
                    }}
                    onBlur={() => {
                        setFocusedItem(selectedItem);
                    }}
                />
            );
        });
    };

    return (
        <Wrapper {...restProps}>
            {focusedItem && (
                <StyledSectionHeader
                    {...focusedItem}
                    productId={productId}
                    variantPickerButtonText={variantPickerButtonText}
                />
            )}

            {(variants.length > 1 || hasMultipleVariantSections) && (
                <Nav>
                    <StyledTruncatedGrid
                        visibleRows={2}
                        cellWidth={Number.parseInt(DisplayTypeSize[displayType].width || '')}
                        cellHeight={Number.parseInt(DisplayTypeSize[displayType].height || '')}
                        ToggleButtonRenderer={({ hiddenItemsCount, ...restProps }) =>
                            displayType === 'Text' ? (
                                <DefaultToggleButton {...restProps} expandText={t(ShowMoreVariantsButton)} />
                            ) : (
                                <DefaultToggleButton {...restProps} hiddenItemsCount={hiddenItemsCount} />
                            )
                        }
                    >
                        {renderVariants()}
                    </StyledTruncatedGrid>
                    <StyledScrollSnapSlider>{renderVariants({ slider: true })}</StyledScrollSnapSlider>
                </Nav>
            )}
        </Wrapper>
    );
}
